import { useState, useEffect, useCallback, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  Share,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";
import { CalendarExportSheet } from "@/modules/agenda/components/CalendarExportSheet";
import { RevealCountdown } from "@/modules/agenda/components/RevealCountdown";
import { RevealHints } from "@/modules/agenda/components/RevealHints";
import { RevealChallenges } from "@/modules/agenda/components/RevealChallenges";
import { RevealManagement } from "@/modules/agenda/components/RevealManagement";
import { useReveal } from "@/modules/agenda/hooks/useReveal";
import { AgendaEvent, AgendaCategory } from "@/types";
import { format, formatDistanceToNow, isPast, isFuture } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Category icons mapping
 */
const CATEGORY_ICONS: Record<AgendaCategory | string, keyof typeof Ionicons.glyphMap> = {
  family_day: "sunny",
  family_weekend: "calendar",
  activity: "game-controller",
  meal: "restaurant",
  game: "trophy",
  ceremony: "ribbon",
  free_time: "cafe",
  custom: "ellipse",
};

/**
 * Event Detail Screen
 */
export default function EventDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile, roles, userTeam } = useAuth();

  const [event, setEvent] = useState<AgendaEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCalendarSheet, setShowCalendarSheet] = useState(false);
  const [showRevealManagement, setShowRevealManagement] = useState(false);
  const [linkedChallenges, setLinkedChallenges] = useState<{
    id: string;
    title: string;
    description: string | null;
    challenge_type: string;
    status: string;
    reveal_targets: string[];
    has_points: boolean;
    points: number;
  }[]>([]);

  const isSuperAdmin = roles.isSuperAdmin;
  const isFamilieAdmin = roles.isFamilieAdmin;
  const isCreator = event?.created_by === profile?.id;

  // Fetch reveal data
  const {
    hints,
    challenges,
    isLoading: isRevealLoading,
    triggerReveal,
    refetch: refetchReveal,
  } = useReveal({ eventId: id || "", enabled: !!event?.has_reveal });

  /**
   * Check if this is a birthday event (synthetic, not in database)
   */
  const isBirthdayEvent = id?.startsWith("birthday-") ?? false;

  /**
   * Check if user can edit this event
   * - Birthday events cannot be edited (they're synthetic)
   * - Super admins can edit all events
   * - Familie admins can edit all family/weekend events
   * - Event creator can edit their own events
   */
  const canEdit = useMemo(() => {
    if (!event || !profile?.id) return false;
    // Birthday events cannot be edited
    if (isBirthdayEvent) return false;
    if (isSuperAdmin) return true;
    if (isFamilieAdmin && (event.event_type === "family" || event.event_type === "weekend")) {
      return true;
    }
    return isCreator;
  }, [event, profile?.id, isSuperAdmin, isFamilieAdmin, isCreator, isBirthdayEvent]);

  /**
   * Parse birthday event ID to extract profile ID and year
   * Format: birthday-{profileId}-{year}
   */
  const parseBirthdayId = (birthdayId: string) => {
    const parts = birthdayId.replace("birthday-", "").split("-");
    // UUID is 5 parts, year is the last part
    const year = parts.pop();
    const profileId = parts.join("-");
    return { profileId, year: parseInt(year || "0", 10) };
  };

  /**
   * Fetch event details
   */
  const fetchEvent = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Handle birthday events (synthetic, not in database)
      if (isBirthdayEvent) {
        const { profileId, year } = parseBirthdayId(id);
        
        // Fetch the profile for this birthday
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("id, full_name, nickname, avatar_url, date_of_birth")
          .eq("id", profileId)
          .single();

        if (profileError || !profileData) {
          throw new Error("Kon profiel niet laden");
        }

        const displayName = profileData.nickname || profileData.full_name || "Onbekend";
        const birthDate = profileData.date_of_birth ? new Date(profileData.date_of_birth) : null;
        const age = birthDate ? year - birthDate.getFullYear() : null;
        
        // Create a synthetic birthday event
        const birthdayEvent: AgendaEvent = {
          id,
          weekend_id: null,
          title: `🎂 ${displayName} is jarig!`,
          description: age && age > 0 ? `${displayName} wordt ${age} jaar` : null,
          location: null,
          location_lat: null,
          location_lng: null,
          starts_at: birthDate 
            ? new Date(year, birthDate.getMonth(), birthDate.getDate()).toISOString()
            : new Date().toISOString(),
          ends_at: birthDate 
            ? new Date(year, birthDate.getMonth(), birthDate.getDate()).toISOString()
            : new Date().toISOString(),
          all_day: true,
          category: "custom",
          icon: "gift",
          color: "#F59E0B",
          is_mandatory: false,
          send_reminder: false,
          reminder_minutes_before: 0,
          game_id: null,
          created_by: profileId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          event_type: "family",
          visibility: "public",
          team_id: null,
          is_recurring: true,
          recurrence_rule: { type: "yearly" },
          parent_event_id: null,
          has_reveal: false,
          reveal_type: null,
          reveal_target: null,
          reveal_at: null,
          reveal_config: null,
          is_revealed: true,
          revealed_for_team_ids: [],
          secret_title: null,
          secret_description: null,
          lottie_animation: "cake",
          notify_on_create: false,
          notify_on_reveal: false,
          custom_notify_at: null,
          created_by_profile: {
            id: profileData.id,
            full_name: profileData.full_name,
            avatar_url: profileData.avatar_url,
          },
        };

        setEvent(birthdayEvent);
        setLinkedChallenges([]);
        setIsLoading(false);
        return;
      }

      // Normal database event fetch
      const { data, error: fetchError } = await supabase
        .from("agenda_events")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      // Fetch creator profile separately if needed
      let eventWithProfile = data as AgendaEvent;
      if (data.created_by) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .eq("id", data.created_by)
          .single();
        
        if (profileData) {
          eventWithProfile = {
            ...eventWithProfile,
            created_by_profile: profileData,
          };
        }
      }

      setEvent(eventWithProfile);

      // Fetch linked challenges (from challenges table)
      const { data: challengesData } = await supabase
        .from("challenges")
        .select("id, title, description, challenge_type, status, reveal_targets, has_points, points")
        .eq("linked_event_id", id);
      
      setLinkedChallenges(challengesData || []);
    } catch (err: any) {
      console.error("Error fetching event:", err);
      setError(err.message || "Kon evenement niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [id, isBirthdayEvent]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  /**
   * Delete event
   */
  const handleDelete = useCallback(() => {
    if (!canEdit) {
      Alert.alert("Fout", "Je hebt geen rechten om dit evenement te verwijderen");
      return;
    }

    Alert.alert(
      "Evenement Verwijderen",
      "Weet je zeker dat je dit evenement wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            try {
              const { error: deleteError } = await supabase
                .from("agenda_events")
                .delete()
                .eq("id", id);

              if (deleteError) {
                throw deleteError;
              }

              // Log the deletion
              if (profile?.id && event) {
                await logActivity({
                  user_id: profile.id,
                  action_type: "agenda.delete",
                  action_category: "agenda",
                  target_type: "agenda_event",
                  target_id: id,
                  target_name: event.title,
                  metadata: {
                    event_type: event.event_type,
                    deleted_by_role: isSuperAdmin ? "super_admin" : isFamilieAdmin ? "familie_admin" : "user",
                    was_creator: isCreator,
                  },
                });
              }

              Alert.alert("Verwijderd", "Evenement is verwijderd", [
                { text: "OK", onPress: () => router.back() },
              ]);
            } catch (err: any) {
              Alert.alert("Fout", err.message || "Kon evenement niet verwijderen");
            }
          },
        },
      ]
    );
  }, [id, router, canEdit, profile?.id, event, isSuperAdmin, isFamilieAdmin, isCreator]);

  /**
   * Navigate to edit screen
   */
  const handleEdit = useCallback(() => {
    if (!canEdit) {
      Alert.alert("Fout", "Je hebt geen rechten om dit evenement te bewerken");
      return;
    }
    router.push(`/agenda/edit/${id}`);
  }, [canEdit, id, router]);

  /**
   * Share event
   */
  const handleShare = useCallback(async () => {
    if (!event) return;

    try {
      const startDate = format(new Date(event.starts_at), "EEEE d MMMM HH:mm", { locale: nl });
      let message = `📅 ${event.title}\n📆 ${startDate}`;

      if (event.location) {
        message += `\n📍 ${event.location}`;
      }

      if (event.description) {
        message += `\n\n${event.description}`;
      }

      await Share.share({
        message,
        title: event.title,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  }, [event]);

  /**
   * Format event time display
   */
  const formatEventTime = (): string => {
    if (!event) return "";

    const startDate = new Date(event.starts_at);

    if (event.all_day) {
      return format(startDate, "EEEE d MMMM yyyy", { locale: nl });
    }

    const startTime = format(startDate, "HH:mm");
    const dateStr = format(startDate, "EEEE d MMMM yyyy", { locale: nl });

    if (event.ends_at) {
      const endDate = new Date(event.ends_at);
      const endTime = format(endDate, "HH:mm");
      return `${dateStr}\n${startTime} - ${endTime}`;
    }

    return `${dateStr}\n${startTime}`;
  };

  /**
   * Get event status badge
   */
  const getEventStatus = () => {
    if (!event) return null;

    const startDate = new Date(event.starts_at);
    const endDate = event.ends_at ? new Date(event.ends_at) : null;

    if (isPast(endDate || startDate)) {
      return { label: "Afgelopen", color: colors.textTertiary, bg: colors.surfaceSecondary };
    }

    if (isFuture(startDate)) {
      const distance = formatDistanceToNow(startDate, { locale: nl, addSuffix: true });
      return { label: `Begint ${distance}`, color: colors.primary, bg: colors.primaryLight };
    }

    return { label: "Nu bezig", color: colors.success, bg: colors.successLight };
  };

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  if (error || !event) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-row items-center px-4 py-3">
          <TouchableOpacity onPress={() => router.back()}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color={colors.error} />
          <Text className="text-lg font-medium mt-4 text-center" style={{ color: colors.text }}>
            {error || "Evenement niet gevonden"}
          </Text>
          <TouchableOpacity onPress={() => router.back()} className="mt-4">
            <Text style={{ color: colors.primary }}>Ga terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const status = getEventStatus();
  const eventColor = event.color || "#7C3AED";
  const eventIcon = event.icon || CATEGORY_ICONS[event.category || "custom"];

  // Check reveal visibility - admins can always see everything
  const isAdmin = isSuperAdmin || isFamilieAdmin;
  const isRevealedForUser =
    event.is_revealed ||
    (userTeam?.id && event.revealed_for_team_ids?.includes(userTeam.id));
  const hasActiveReveal = event.has_reveal && !isRevealedForUser;

  // What should be hidden based on reveal_target? Default to "location" if not set
  const revealTarget = event.reveal_target || "location";
  const shouldHideTitle = hasActiveReveal && !isAdmin && (revealTarget === "event" || revealTarget === "all");
  const shouldHideLocation = hasActiveReveal && !isAdmin && (revealTarget === "location" || revealTarget === "all");
  const shouldHideDetails = hasActiveReveal && !isAdmin && (revealTarget === "details" || revealTarget === "all");

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity onPress={() => router.back()} accessibilityLabel="Terug">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => setShowCalendarSheet(true)}
            className="p-2"
            accessibilityLabel="Toevoegen aan agenda"
          >
            <Ionicons name="calendar-outline" size={22} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleShare} className="p-2" accessibilityLabel="Delen">
            <Ionicons name="share-outline" size={22} color={colors.text} />
          </TouchableOpacity>
          {isAdmin && event.has_reveal && (
            <TouchableOpacity
              onPress={() => setShowRevealManagement(true)}
              className="p-2"
              accessibilityLabel="Reveal beheren"
            >
              <Ionicons name="eye-outline" size={22} color={colors.warning} />
            </TouchableOpacity>
          )}
          {canEdit && (
            <>
              <TouchableOpacity onPress={handleEdit} className="p-2" accessibilityLabel="Bewerken">
                <Ionicons name="pencil-outline" size={22} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={handleDelete} className="p-2" accessibilityLabel="Verwijderen">
                <Ionicons name="trash-outline" size={22} color={colors.error} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Hero section with color */}
        <View
          className="px-6 py-8"
          style={{ backgroundColor: `${eventColor}15` }}
        >
          <View className="flex-row items-center mb-4">
            <View
              className="w-12 h-12 rounded-full items-center justify-center"
              style={{ backgroundColor: eventColor }}
            >
              <Ionicons name={eventIcon as any} size={24} color="#FFFFFF" />
            </View>
            {status && (
              <View
                className="ml-auto px-3 py-1.5 rounded-full"
                style={{ backgroundColor: status.bg }}
              >
                <Text className="text-sm font-medium" style={{ color: status.color }}>
                  {status.label}
                </Text>
              </View>
            )}
          </View>

          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            {shouldHideTitle ? (event.secret_title || "🔒 Verborgen evenement") : event.title}
          </Text>

          {/* Badges */}
          <View className="flex-row flex-wrap mt-3">
            {event.is_mandatory && (
              <View
                className="px-3 py-1 rounded-full mr-2 mb-2"
                style={{ backgroundColor: colors.warningLight }}
              >
                <Text className="text-xs font-medium" style={{ color: colors.warning }}>
                  Verplicht
                </Text>
              </View>
            )}
            {event.visibility === "team_only" && (
              <View
                className="px-3 py-1 rounded-full mr-2 mb-2"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                  Alleen je team
                </Text>
              </View>
            )}
            {event.visibility === "admin_only" && (
              <View
                className="px-3 py-1 rounded-full mr-2 mb-2"
                style={{ backgroundColor: isDark ? "#78350F" : "#FEF3C7" }}
              >
                <Text
                  className="text-xs font-medium"
                  style={{ color: isDark ? "#FCD34D" : "#92400E" }}
                >
                  Alleen admins
                </Text>
              </View>
            )}
            {event.has_reveal && (
              <View
                className="px-3 py-1 rounded-full mr-2 mb-2"
                style={{ backgroundColor: event.is_revealed ? colors.successLight : colors.surfaceSecondary }}
              >
                <Text
                  className="text-xs font-medium"
                  style={{ color: event.is_revealed ? colors.success : colors.textSecondary }}
                >
                  {event.is_revealed ? "✓ Onthuld" : "⏳ Nog verborgen"}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Details */}
        <View className="px-4 py-6">
          {/* Date & Time */}
          <View className="flex-row items-start mb-4">
            <View
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons name="time" size={20} color={colors.primary} />
            </View>
            <View className="ml-4 flex-1">
              <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                Datum & Tijd
              </Text>
              <Text className="mt-1" style={{ color: colors.text }}>
                {formatEventTime()}
              </Text>
              {event.all_day && (
                <Text className="text-sm mt-1" style={{ color: colors.textTertiary }}>
                  Hele dag
                </Text>
              )}
            </View>
          </View>

          {/* Location */}
          {!shouldHideLocation && event.location && (
            <View className="flex-row items-start mb-4">
              <View
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.successLight }}
              >
                <Ionicons name="location" size={20} color={colors.success} />
              </View>
              <View className="ml-4 flex-1">
                <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                  Locatie
                </Text>
                <Text className="mt-1" style={{ color: colors.text }}>
                  {event.location}
                </Text>
              </View>
            </View>
          )}

          {/* Description */}
          {!shouldHideDetails && event.description && (
            <View className="flex-row items-start mb-4">
              <View
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="document-text" size={20} color={colors.textSecondary} />
              </View>
              <View className="ml-4 flex-1">
                <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                  Beschrijving
                </Text>
                <Text className="mt-1" style={{ color: colors.text }}>
                  {event.description}
                </Text>
              </View>
            </View>
          )}

          {/* Hidden reveal message - show what's being hidden */}
          {hasActiveReveal && !isAdmin && (shouldHideTitle || shouldHideLocation || shouldHideDetails) && (
            <View
              className="p-4 rounded-xl"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <View className="flex-row items-center">
                <Ionicons name="lock-closed" size={24} color={colors.primary} />
                <Text className="ml-3 font-semibold" style={{ color: colors.primary }}>
                  {shouldHideLocation && !shouldHideTitle && !shouldHideDetails
                    ? "Locatie verborgen"
                    : shouldHideDetails && !shouldHideTitle && !shouldHideLocation
                    ? "Details verborgen"
                    : "Onderdelen verborgen"}
                </Text>
              </View>
              <Text className="mt-2" style={{ color: colors.primaryText }}>
                {event.secret_description || "Dit wordt later onthuld. Houd de app in de gaten!"}
              </Text>
              {event.reveal_at && (
                <Text className="mt-2 font-medium" style={{ color: colors.primary }}>
                  Onthulling: {format(new Date(event.reveal_at), "d MMMM HH:mm", { locale: nl })}
                </Text>
              )}
            </View>
          )}

          {/* Reveal System Components - shown when event has reveal enabled */}
          {event.has_reveal && (
            <View className="mt-6">
              {/* Countdown - show when not revealed and there's a reveal date */}
              {!event.is_revealed && event.reveal_at && (
                <RevealCountdown
                  revealAt={event.reveal_at}
                  onReveal={async () => {
                    const success = await triggerReveal(event.id);
                    if (success) {
                      // Refresh event data
                      setEvent((prev) => prev ? { ...prev, is_revealed: true } : null);
                      fetchEvent(); // Refetch to get updated data
                      // Log reveal
                      if (profile?.id) {
                        await logActivity({
                          user_id: profile.id,
                          action_type: "agenda.reveal",
                          action_category: "agenda",
                          target_type: "agenda_event",
                          target_id: event.id,
                          target_name: event.title,
                        });
                      }
                    }
                  }}
                  lottieAnimation={event.lottie_animation}
                  isAdmin={isAdmin}
                />
              )}

              {/* Hints - always show when there are hints */}
              {hints.length > 0 && (
                <View className="mt-4">
                  <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
                    🔍 Hints
                  </Text>
                  <RevealHints
                    hints={hints.map((h) => ({
                      id: h.id,
                      text: h.hint_text,
                      imageUrl: h.hint_image_url || undefined,
                      releaseAt: h.release_at,
                      isReleased: h.is_released,
                    }))}
                    onRefresh={refetchReveal}
                  />
                </View>
              )}

              {/* Challenges - always show when there are challenges */}
              {challenges.length > 0 && (
                <View className="mt-4">
                  <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
                    🎯 Uitdagingen
                  </Text>
                  <RevealChallenges
                    challenges={challenges.map((c) => ({
                      id: c.id,
                      title: c.title,
                      description: c.description || undefined,
                      type: c.challenge_type,
                      isCompleted: c.is_completed,
                      completedByTeamId: c.completed_by_team_id || undefined,
                      deadline: c.deadline || undefined,
                      rewardText: c.reward_text || undefined,
                    }))}
                    eventId={event.id}
                    isAdmin={isAdmin}
                    onRefresh={refetchReveal}
                  />
                </View>
              )}
            </View>
          )}

          {/* Linked Challenges (from standalone challenges table) */}
          {linkedChallenges.length > 0 && (
            <View className="mt-6">
              <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
                🔓 Uitdagingen om te ontgrendelen
              </Text>
              <View
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: colors.surface }}
              >
                {linkedChallenges.map((challenge, index) => (
                  <TouchableOpacity
                    key={challenge.id}
                    className="p-4 flex-row items-center"
                    style={{
                      borderTopWidth: index > 0 ? 1 : 0,
                      borderTopColor: colors.border,
                    }}
                    activeOpacity={0.7}
                    onPress={() => router.push(`/(tabs)/games/challenge/${challenge.id}`)}
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor:
                          challenge.status === "active"
                            ? colors.successLight
                            : colors.surfaceSecondary,
                      }}
                    >
                      <Ionicons
                        name={
                          challenge.challenge_type === "photo"
                            ? "camera"
                            : challenge.challenge_type === "quiz"
                            ? "help-circle"
                            : "flash"
                        }
                        size={20}
                        color={
                          challenge.status === "active"
                            ? colors.success
                            : colors.textSecondary
                        }
                      />
                    </View>
                    <View className="flex-1">
                      <Text className="font-medium" style={{ color: colors.text }}>
                        {challenge.title}
                      </Text>
                      {challenge.reveal_targets && challenge.reveal_targets.length > 0 && (
                        <Text className="text-sm" style={{ color: colors.textSecondary }}>
                          Ontgrendelt:{" "}
                          {challenge.reveal_targets
                            .map((t) =>
                              t === "location"
                                ? "Locatie"
                                : t === "title"
                                ? "Titel"
                                : t === "description"
                                ? "Beschrijving"
                                : t
                            )
                            .join(", ")}
                        </Text>
                      )}
                      {challenge.has_points && challenge.points > 0 && (
                        <View className="flex-row items-center mt-1">
                          <Ionicons name="star" size={12} color="#F59E0B" />
                          <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                            {challenge.points} punten
                          </Text>
                        </View>
                      )}
                    </View>
                    <View
                      className="px-2 py-1 rounded-full"
                      style={{
                        backgroundColor:
                          challenge.status === "active"
                            ? colors.successLight
                            : challenge.status === "completed"
                            ? colors.primaryLight
                            : colors.surfaceSecondary,
                      }}
                    >
                      <Text
                        className="text-xs font-medium"
                        style={{
                          color:
                            challenge.status === "active"
                              ? colors.success
                              : challenge.status === "completed"
                              ? colors.primary
                              : colors.textTertiary,
                        }}
                      >
                        {challenge.status === "active"
                          ? "Actief"
                          : challenge.status === "completed"
                          ? "Voltooid"
                          : challenge.status === "draft"
                          ? "Concept"
                          : "Gearchiveerd"}
                      </Text>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={colors.textTertiary}
                      style={{ marginLeft: 8 }}
                    />
                  </TouchableOpacity>
                ))}
              </View>
              {!event.is_revealed && linkedChallenges.some((c) => c.status === "active") && (
                <Text
                  className="text-sm mt-3 text-center"
                  style={{ color: colors.textSecondary }}
                >
                  Voltooi de actieve uitdaging(en) om dit evenement te ontgrendelen!
                </Text>
              )}
            </View>
          )}

          {/* Created by */}
          {event.created_by_profile && (
            <View className="flex-row items-start mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <View
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="person" size={20} color={colors.textSecondary} />
              </View>
              <View className="ml-4 flex-1">
                <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                  Aangemaakt door
                </Text>
                <Text className="mt-1" style={{ color: colors.text }}>
                  {event.created_by_profile.full_name}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* Add to Calendar CTA */}
        <View className="px-4 pb-8">
          <TouchableOpacity
            onPress={() => setShowCalendarSheet(true)}
            className="flex-row items-center justify-center py-4 rounded-xl"
            style={{ backgroundColor: colors.primary }}
          >
            <Ionicons name="calendar-outline" size={20} color="#FFFFFF" />
            <Text className="ml-2 font-semibold text-white">
              Toevoegen aan mijn agenda
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Calendar Export Sheet */}
      <CalendarExportSheet
        visible={showCalendarSheet}
        onClose={() => setShowCalendarSheet(false)}
        event={event}
      />

      {/* Reveal Management Modal (Admin only) */}
      {isAdmin && event.has_reveal && (
        <RevealManagement
          visible={showRevealManagement}
          onClose={() => {
            setShowRevealManagement(false);
            refetchReveal();
          }}
          eventId={event.id}
          eventTitle={event.title}
        />
      )}
    </SafeAreaView>
  );
}

